import { FC, ReactNode, useState } from 'react';

import { SerializedStyles } from '@emotion/react';
import { Button } from '@mui/material';
import logger from 'logger';

export interface ZendeskLoaderProps {
  children?: ReactNode;
  loadingIndicator?: ReactNode | string;
  hideWhenOpen?: boolean;
  styles?: SerializedStyles;
}

/**
 * Constructs an MUI Button that loads the Zendesk chat.
 */
const ZendeskLoader: FC<ZendeskLoaderProps> = ({
  children,
  loadingIndicator = 'Loading…',
  hideWhenOpen = false,
  styles = {} as SerializedStyles,
}) => {
  const [isScriptAdded, setIsScriptAdded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const WIDGET_OPENED_EVENT = 'Web Widget Opened';

  // Load the Zendesk script if it hasn't already been loaded
  const addZendeskScript = () => {
    if (!isScriptAdded) {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      require('./zendeskScript');
    }
    setIsScriptAdded(true);
  };

  const initializeZendesk = () =>
    new Promise((resolve, reject) => {
      if (isInitialized) {
        resolve(true);
      } else {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        zE(async () => {
          try {
            // https://support.zendesk.com/hc/en-us/articles/115007912068-Using-the-Chat-widget-JavaScript-API
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            $zopim(() => {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              zE('webWidget', 'hide');
              addWidgetEventHandlers();
              setIsInitialized(true);
              resolve(true);
            });
          } catch (error: any) {
            // @todo Add UI feedback for this failure.
            logger.error({
              msg: 'Failed to load Zendesk',
              noticable: error,
            });
            reject(true);
          }
        });
      }
    });

  const addWidgetEventHandlers = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    zE('webWidget:on', 'close', () => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      zE('webWidget', 'hide');
      setIsWidgetOpen(false);
    });
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    zE('webWidget:on', 'userEvent', (event) => {
      if (event.action === WIDGET_OPENED_EVENT) {
        setIsLoading(false);
        setIsWidgetOpen(true);
      }
    });
  };

  const openWidget = () => {
    // https://developer.zendesk.com/embeddables/docs/widget/core#open
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    zE('webWidget', 'show');
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    zE('webWidget', 'open');
  };

  const loadZendesk = async () => {
    setIsLoading(true);
    addZendeskScript();
    await initializeZendesk();
    openWidget();
  };

  const renderZendeskLoader = () => (
    <Button
      css={styles}
      variant="text"
      onClick={loadZendesk}
      disabled={isLoading || isWidgetOpen}
    >
      {isLoading ? loadingIndicator : children}
    </Button>
  );
  return hideWhenOpen && isWidgetOpen ? null : renderZendeskLoader();
};

export default ZendeskLoader;
